import * as React from "react";
import { Check } from "lucide-react";
import { Namespace, useTypedT, type TranslationKey } from "@racoon/i18n";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@racoon/ui";
import type { Agreement, FieldSource, SourceOption } from "../../hooks/ttn/ttnDraftUtils";

/**
 * A field's border colour says where its value comes from: typed in, measured
 * (GPS / sensor / track), the scale ticket, the driver's buttons, or nothing actual but
 * the plan. On an approved TTN the colour is the source the stored value still
 * agrees with — the same legend: blue when it agrees with nothing that can be
 * checked (typed in), plain when there is nothing to check it against.
 *
 * `!`: `cn` is a plain join, and focus must still win over the colour.
 */
type SourceTone = "manual" | "measured" | "scale" | "driver" | "plan";

const TONE_OF: Record<FieldSource, SourceTone | undefined> = {
  manual: "manual",
  gps: "measured",
  scale: "scale",
  sensor: "measured",
  track: "measured",
  driver: "driver",
  plan: "plan",
  saved: undefined,
};

// Figma Border/information, success, AI, attention, error.
const TONE_BORDER: Record<SourceTone, string> = {
  manual: "!border-ds-border-information",
  measured: "!border-ds-border-success",
  scale: "!border-ds-border-ai",
  driver: "!border-ds-border-attention",
  plan: "!border-ds-border-error",
};
const FOCUS_BORDER =
  "focus-within:!border-ds-border-action-hover focus:!border-ds-border-action-hover";

const SOURCE_LABEL = {
  manual: "ttnSourceManual",
  gps: "ttnSourceGps",
  scale: "ttnSourceScale",
  sensor: "ttnSourceSensor",
  track: "ttnSourceTrack",
  driver: "ttnSourceDriver",
  plan: "ttnSourcePlan",
  saved: "ttnSourceSaved",
} as const satisfies Record<FieldSource, string>;

/** A value agreeing with nothing checkable reads as typed in: the manual tone. */
const toneOfAgreement = (agreement: Agreement): SourceTone | undefined =>
  agreement === "none" ? "manual" : agreement === "unknown" ? undefined : TONE_OF[agreement];

const classesOf = (tone: SourceTone | undefined) =>
  tone ? `${TONE_BORDER[tone]} ${FOCUS_BORDER}` : "";

/**
 * Border classes and hover hint for a field. `agreement` is set only once the
 * TTN is approved and the field shows the stored value — it then picks the
 * colour, since the stored value's own origin is not recorded.
 */
export const useSourceHint = () => {
  const t = useTypedT(Namespace.Routes);
  return React.useCallback(
    (source: FieldSource | undefined, agreement?: Agreement) => {
      if (agreement) {
        const title =
          agreement === "none"
            ? t("ttnAgreesNone")
            : agreement === "unknown"
              ? t("ttnAgreesUnknown")
              : t("ttnAgreesWith", { source: t(SOURCE_LABEL[agreement]) });
        return { border: classesOf(toneOfAgreement(agreement)), title };
      }
      const tone = source && TONE_OF[source];
      return {
        border: classesOf(tone),
        title: source ? t(SOURCE_LABEL[source]) : undefined,
      };
    },
    [t],
  );
};

const Swatch: React.FC<{ tone: SourceTone | undefined }> = ({ tone }) => (
  <span
    className={`h-4 w-4 shrink-0 rounded-[4px] border-2 border-ds-border-primary ${tone ? TONE_BORDER[tone] : ""}`}
  />
);

const LEGEND: { key: string; tone?: SourceTone; label: TranslationKey<Namespace.Routes> }[] = [
  { key: "manual", tone: "manual", label: "ttnSourceManual" },
  { key: "measured", tone: "measured", label: "ttnSourceMeasured" },
  { key: "scale", tone: "scale", label: "ttnSourceScale" },
  { key: "driver", tone: "driver", label: "ttnSourceDriver" },
  { key: "plan", tone: "plan", label: "ttnSourcePlan" },
  // Plain border: the value stored on the approved TTN.
  { key: "saved", label: "ttnSourceSaved" },
];

/** What the border colours mean — the same on an open and an approved TTN. */
export const SourceLegend: React.FC<{ className?: string }> = ({ className }) => {
  const t = useTypedT(Namespace.Routes);
  return (
    <div
      className={`flex shrink-0 flex-wrap items-center gap-6 p-2 text-xs font-medium leading-4 text-ds-text-secondary ${className ?? ""}`}
    >
      {LEGEND.map(({ key, tone, label }) => (
        <span key={key} className="flex items-center gap-1.5">
          <Swatch tone={tone} />
          {t(label)}
        </span>
      ))}
    </div>
  );
};

/**
 * Right-click on a TTN field: set it to what one of its sources says, or drop
 * the edit so the form fills it in itself again. Just the field while disabled.
 */
export const SourceMenu: React.FC<{
  options: SourceOption[] | undefined;
  /** The value shown: the source that says the same is ticked, else "typed in". */
  value: SourceOption["value"] | undefined;
  /** Where the shown value comes from: preferred for the tick when sources agree. */
  current: FieldSource | undefined;
  /** Whether the field holds an edit, typed or picked: "reset" drops it. */
  isEdited: boolean;
  format: (value: SourceOption["value"]) => string;
  onPick: (option: SourceOption) => void;
  onReset: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({
  options: allOptions = [],
  value,
  current,
  isEdited,
  format,
  onPick,
  onReset,
  disabled,
  children,
}) => {
  const t = useTypedT(Namespace.Routes);
  // "Approved" is what the field already holds: as a value it is either one of
  // the sources' or typed in, and either way it is listed as such below.
  const options = allOptions.filter((option) => option.source !== "saved");
  if (disabled || (options.length === 0 && !isEdited)) return <>{children}</>;
  // Compared as shown: a time to the minute, a number to its two decimals.
  const shown = value == null ? undefined : format(value);
  // Two sources can read the same: the one the value was taken from is ticked.
  const sameAsShown = (option: SourceOption) => format(option.value) === shown;
  const matched =
    options.find((option) => option.source === current && sameAsShown(option)) ??
    options.find(sameAsShown);
  return (
    <ContextMenu>
      {/* `contents`: the wrapper takes the right-click without touching the layout. */}
      <ContextMenuTrigger asChild>
        <div className="contents">{children}</div>
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-80">
        {shown !== undefined && !matched && (
          <ContextMenuItem className="flex items-center gap-3">
            <Swatch tone="manual" />
            <span className="flex-1">{t("ttnSourceManual")}</span>
            <span className="text-text-secondary">{shown}</span>
            <Check className="h-4 w-4 shrink-0" />
          </ContextMenuItem>
        )}
        {options.map((option) => (
          <ContextMenuItem
            key={option.source}
            className="flex items-center gap-3"
            onSelect={() => onPick(option)}
          >
            <Swatch tone={TONE_OF[option.source]} />
            <span className="flex-1">{t(SOURCE_LABEL[option.source])}</span>
            <span className="text-text-secondary">{format(option.value)}</span>
            <Check className={`h-4 w-4 shrink-0 ${option === matched ? "" : "invisible"}`} />
          </ContextMenuItem>
        ))}
        {isEdited && (
          <>
            {options.length > 0 && <ContextMenuSeparator />}
            <ContextMenuItem onSelect={onReset}>{t("ttnResetManual")}</ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};
